import { useCallback, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import { FocusTrap } from 'focus-trap-react';

// Panel enter/exit animation presets. Shared here (rather than each caller
// re-declaring an identical local const) so the entity-style panels used by
// EntityDetailModal/PlaceDetailModal, and the confirm dialogs used by
// CollectionDetail/MyCollections, stay in lockstep by construction.
export const DEFAULT_PANEL_MOTION = {
  initial: { opacity: 0, scale: 0.95, y: 20 },
  animate: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: 20 },
  transition: { type: 'spring', damping: 25, stiffness: 300 },
};

export const ENTITY_PANEL_MOTION = {
  initial: { opacity: 0, y: 40, scale: 0.97 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: 40, scale: 0.97 },
  transition: { type: 'spring', damping: 30, stiffness: 400 },
};

export const CONFIRM_PANEL_MOTION = {
  initial: { scale: 0.95, opacity: 0 },
  animate: { scale: 1, opacity: 1 },
  exit: { scale: 0.95, opacity: 0 },
};

// Module-level stack of currently-mounted Modal instances (by an opaque id),
// topmost = most recently mounted. Modals can nest (e.g. CollectionPickerModal
// opened from inside ItemDetailModal), and each instance registers its own
// Escape listener — without stack-awareness, one Escape press would close
// every mounted Modal at once instead of just the topmost.
let modalStack = [];
let nextStackId = 0;

/**
 * Whether any <Modal> is currently mounted anywhere in the app. Used by
 * global-scope listeners (e.g. Gallery's paste-to-upload, COL-96) that must
 * not act while the user is inside a modal — a global paste handler with no
 * awareness of an open UploadModal would otherwise silently discard a
 * staged-but-not-yet-submitted file, or fire an unrelated upload while the
 * user is just viewing an ItemDetailModal.
 */
export function isAnyModalOpen() {
  return modalStack.length > 0;
}

/**
 * Shared modal primitive — dialog semantics, focus trap, Escape-to-close,
 * backdrop-click-to-close, and focus-return-to-trigger. One implementation
 * backing every modal in the app (COL-89).
 *
 * Open/closed state is NOT owned here — presence in the render tree IS the
 * open state, matching the app's existing convention of
 * `{condition && <SomeModal ... />}` inside <AnimatePresence>. That keeps
 * this primitive controllable by a parent (e.g. URL state in COL-87)
 * without Modal needing its own isOpen/setOpen bookkeeping.
 *
 * Scroll-lock is intentionally NOT handled here (already solved consistently
 * by PR #304 via the `useScrollLock` hook) — callers that need it keep
 * calling `useScrollLock` themselves and attach its ref to whichever DOM
 * node is actually their scrollable region, then pass a wrapped `onClose`
 * that unlocks first.
 *
 * @param {Object} props
 * @param {() => void} props.onClose - Called on Escape or backdrop click.
 *   Callers that scroll-lock should unlock inside this before calling their
 *   real onClose.
 * @param {string} props.labelledBy - id of the heading element (rendered by
 *   the caller inside `children`) that names this dialog. Required for the
 *   aria-labelledby contract — use React's `useId()` to generate a stable id
 *   and put it on the real heading element.
 * @param {React.ReactNode} props.children - Dialog panel content.
 * @param {string} [props.className='modal-container'] - Class(es) for the
 *   panel element (the thing that gets role="dialog").
 * @param {string} [props.overlayClassName='modal-overlay'] - Class(es) for
 *   the backdrop element.
 * @param {Object} [props.panelStyle] - Inline style passthrough for the
 *   panel element (for modals that were previously styled inline).
 * @param {boolean} [props.closeOnOverlayClick=true] - Whether clicking the
 *   backdrop (outside the panel) closes the modal.
 * @param {Object} [props.panelMotionProps] - Overrides for the panel's
 *   motion initial/animate/exit/transition (panels differ: scale+y vs. y
 *   only, etc.) — defaults to a scale+y spring.
 * @param {React.MutableRefObject|Function} [props.panelRef] - Optional
 *   external ref to the panel DOM node, for callers whose scroll-lock (or
 *   other DOM need) targets the panel itself rather than an inner element.
 * @param {Object} [props.focusTrapOptions] - Extra focus-trap-react options,
 *   merged over this primitive's defaults (e.g. tests pass
 *   `{ tabbableOptions: { displayCheck: 'none' } }` since jsdom can't
 *   compute layout for focus-trap's real visibility check).
 */
export default function Modal({
  onClose,
  labelledBy,
  children,
  className = 'modal-container',
  overlayClassName = 'modal-overlay',
  panelStyle,
  closeOnOverlayClick = true,
  panelMotionProps = DEFAULT_PANEL_MOTION,
  panelRef,
  focusTrapOptions,
}) {
  const internalPanelRef = useRef(null);

  const setPanelRef = useCallback((node) => {
    internalPanelRef.current = node;
    if (typeof panelRef === 'function') panelRef(node);
    else if (panelRef) panelRef.current = node;
  }, [panelRef]);

  // Register this instance on the shared modal stack for its whole mounted
  // lifetime (id assigned once, not per render).
  const stackIdRef = useRef(null);
  if (stackIdRef.current === null) {
    stackIdRef.current = ++nextStackId;
  }
  useEffect(() => {
    const id = stackIdRef.current;
    modalStack.push(id);
    return () => {
      modalStack = modalStack.filter((x) => x !== id);
    };
  }, []);

  // Escape closes — but only the topmost modal on the stack, so opening a
  // nested modal (e.g. CollectionPickerModal from inside ItemDetailModal)
  // and pressing Escape closes just that one, not both at once.
  //
  // Handled directly (not via focus-trap's escapeDeactivates/onDeactivate)
  // because focus-trap-react only reads focusTrapOptions once on first
  // render and explicitly warns against wiring onDeactivate to
  // state-affecting callbacks — under React 18 StrictMode's dev-only
  // mount/unmount/remount cycle that would fire onClose immediately after
  // opening.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (modalStack[modalStack.length - 1] !== stackIdRef.current) return;
      e.stopPropagation();
      onCloseRef.current();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleOverlayClick = useCallback((e) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  }, [closeOnOverlayClick, onClose]);

  return (
    <motion.div
      className={overlayClassName}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleOverlayClick}
    >
      <FocusTrap
        focusTrapOptions={{
          escapeDeactivates: false,
          clickOutsideDeactivates: false,
          // The backdrop is intentionally OUTSIDE the trap's container (the
          // panel). Without this, focus-trap's default outside-click guard
          // swallows the click before our own onClick (below) ever runs,
          // silently breaking backdrop-click-to-close.
          allowOutsideClick: true,
          fallbackFocus: () => internalPanelRef.current,
          ...focusTrapOptions,
        }}
      >
        <motion.div
          ref={setPanelRef}
          className={className}
          style={panelStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          onClick={(e) => e.stopPropagation()}
          {...panelMotionProps}
        >
          {children}
        </motion.div>
      </FocusTrap>
    </motion.div>
  );
}
